import { ambianceFileUrl } from '@/lib/api';
import type {
  ExperienceFont,
  ExperienceScale,
  ResolvedExperienceTheme,
  ResolvedVisual,
} from '@matter_extractor/shared';
import type { CSSProperties } from 'react';
import type { ReaderFormat } from './contracts';

/**
 * The theme, turned into CSS CUSTOM PROPERTIES (§9.4).
 *
 * « Aucun CSS libre, à aucun niveau » cuts both ways: the operator writes no
 * style, and neither does this component. Every value an experience can change
 * leaves this module as a `--lecteur-*` property on the reader's root, and
 * `reader.css` is the only place that spends them. So the whole of what a theme
 * can do to a page is this file plus that stylesheet, and a rule that wanted a
 * sixth colour would have to add a token first.
 *
 * Pure on purpose: the properties are what the tests assert on, and jsdom has
 * no layout to measure.
 */

/* ------------------------------------------------------------- the families */

/**
 * The five families of §9.4 (H24), each with its fallback stack.
 *
 * The stack matters: a face is loaded on demand (`fonts.ts`), so the first
 * paint of a page whose theme names Lora happens in the fallback, and a serif
 * theme falling back to a sans would reflow into a different page. Titillium
 * Web is the only one already served by `styles.css` - it is the admin's own
 * face and the PMS default - so it never needs loading.
 */
export const READER_FONT_STACK: Readonly<Record<ExperienceFont, string>> = {
  'titillium-web': "'Titillium Web', 'Segoe UI', system-ui, sans-serif",
  'source-sans-3': "'Source Sans 3', 'Segoe UI', system-ui, sans-serif",
  lora: "'Lora', Georgia, 'Times New Roman', serif",
  'playfair-display': "'Playfair Display', Georgia, 'Times New Roman', serif",
  caveat: "'Caveat', 'Segoe Script', cursive",
};

/* ------------------------------------------------------------- the two rulers */

/**
 * What a format settles: the reading column, the rhythm and the three type
 * steps. Read off the two mockup plates - `Lecteur.dc.html` for the phone,
 * the preview of `EditeurPageEcran.dc.html` for the screen.
 *
 * A format is not a breakpoint (`contracts.ts`): the page is authored for one
 * or the other and the editor switches it by hand, so these numbers are fixed
 * rather than clamped against a viewport.
 *
 * `choiceColumn` is the column of a page of CHOICE (issue #168): the maquette
 * `ChoixEcran` lays the consigne and the two cards of a Noyau block over
 * 960 px, and two texts of a specialist side by side in the 592 px the reading
 * column leaves are two cards of thirty lines, one under the edge of the
 * window. Both widths hold their padding (`box-sizing: border-box`), so 1008
 * is those 960 between the two paddings of 24.
 */
type FormatRuler = {
  readonly column: string;
  readonly choiceColumn: string;
  readonly gap: string;
  readonly padBlock: string;
  readonly padInline: string;
  readonly small: readonly [string, string];
  readonly normal: readonly [string, string];
  readonly large: readonly [string, string];
};

const FORMAT: Readonly<Record<ReaderFormat, FormatRuler>> = {
  phone: {
    column: '100%',
    choiceColumn: '100%',
    gap: '20px',
    padBlock: '28px',
    padInline: '24px',
    small: ['14px', '20px'],
    normal: ['16px', '24px'],
    large: ['28px', '36px'],
  },
  screen: {
    column: '640px',
    choiceColumn: '1008px',
    gap: '24px',
    padBlock: '32px',
    padInline: '24px',
    small: ['15px', '22px'],
    normal: ['17px', '26px'],
    large: ['32px', '40px'],
  },
};

/** The three button sizes of §9.4: height, horizontal padding, label size. */
const BUTTON_SIZE: Readonly<Record<ExperienceScale, readonly [string, string, string]>> = {
  small: ['40px', '20px', '14px'],
  normal: ['48px', '28px', '16px'],
  large: ['56px', '34px', '18px'],
};

/** The three shapes of §9.4. A pill is a radius nothing can reach, not a guess. */
const BUTTON_RADIUS: Readonly<Record<ResolvedExperienceTheme['buttonShape'], string>> = {
  square: '0px',
  rounded: '8px',
  pill: '999px',
};

/* -------------------------------------------------------- the type of a block */

/*
 * Which face and which step a block's words take is settled by ONE token.
 *
 * `resolveTheme` merges the theme's `textScale` and the block's into a single
 * value, so it says both how big a block reads and, at `large`, that it reads
 * as a TITLE - in the title face. That is the mechanism the default template
 * leans on: its first block is a Texte carrying `textScale: 'large'`, its own
 * comment says « au style de titre est un token VISUEL et pas un nœud de texte
 * riche », and the mockup paints that very block in Playfair Display. §9.2
 * closes the rich text on paragraphs and lists, with no heading, so this is the
 * only path from « ce bloc est un titre » to a title on screen.
 *
 * The rule itself is three lines of `reader.css`
 * (`.lecteur-bloc[data-scale='large'] .lecteur-texte`), because nothing in TS
 * has to branch on it. The consequence, and it is deliberate: a theme whose own
 * `textScale` is `large` makes every block a title. It is a visible, undoable
 * setting, and the default is `normal`.
 */

/* --------------------------------------------------------------- the motion */

/** Whether the entry animations of §9.3 play at all. */
export type ReaderMotion = 'full' | 'reduced';

/**
 * « Une case " réduire les animations " qui respecte le réglage système de la
 * personne » (§9.4).
 *
 * The token says whether the theme DEFERS to the system, not whether motion is
 * reduced: ticked - the default - a person who asked their system for less
 * movement gets a page that appears at once; unticked, the experience animates
 * whatever the system says. Either way « les délais entre les temps restent »,
 * which is `reader-play.ts`'s business and not this function's.
 */
export function readerMotion(
  theme: Pick<ResolvedExperienceTheme, 'reduceMotion'>,
  prefersReducedMotion: boolean,
): ReaderMotion {
  return theme.reduceMotion && prefersReducedMotion ? 'reduced' : 'full';
}

/* ----------------------------------------------------------- the properties */

/**
 * Every `--lecteur-*` property the root carries, as a plain record.
 *
 * A record and not a `CSSProperties`: React's style type has no index
 * signature for custom properties, and a test that asserts on one wants a
 * string map rather than a cast. `page-reader.tsx` does the single cast at the
 * point it hands the map to the DOM.
 */
export type ReaderCssVariables = Record<string, string>;

/**
 * The theme of one PAGE, resolved, as custom properties (§9.4).
 *
 * `visual` is what `resolveTheme(theme, page.visual)` answered: every token has
 * a value, so nothing here branches on an absent one.
 *
 * The background image goes in as a `url(...)`, and the id inside it is
 * percent-encoded by `ambianceFileUrl` - a quote or a parenthesis in an id
 * could otherwise close the function and write a declaration of its own, which
 * is the one place a media file could have become free CSS.
 */
export function readerThemeStyle(visual: ResolvedVisual, format: ReaderFormat): ReaderCssVariables {
  const ruler = FORMAT[format];
  const [buttonHeight, buttonPad, buttonFont] = BUTTON_SIZE[visual.buttonSize];
  const image =
    visual.backgroundKind === 'image' && visual.backgroundImageAssetId !== null
      ? `url("${ambianceFileUrl(visual.backgroundImageAssetId)}")`
      : 'none';

  return {
    '--lecteur-font-title': READER_FONT_STACK[visual.titleFont],
    '--lecteur-font-text': READER_FONT_STACK[visual.textFont],

    '--lecteur-color-background': visual.background,
    '--lecteur-color-text': visual.text,
    '--lecteur-color-muted': visual.muted,
    '--lecteur-color-accent': visual.accent,
    '--lecteur-color-button': visual.buttonBackground,
    '--lecteur-color-button-text': visual.buttonText,
    '--lecteur-color-ai-bubble': visual.aiBubble,
    '--lecteur-color-person-bubble': visual.personBubble,

    '--lecteur-background-image': image,
    '--lecteur-veil': String(visual.backgroundVeil),

    '--lecteur-button-height': buttonHeight,
    '--lecteur-button-pad': buttonPad,
    '--lecteur-button-font': buttonFont,
    '--lecteur-button-radius': BUTTON_RADIUS[visual.buttonShape],

    '--lecteur-column': ruler.column,
    '--lecteur-column-choice': ruler.choiceColumn,
    '--lecteur-gap': ruler.gap,
    '--lecteur-pad-block': ruler.padBlock,
    '--lecteur-pad-inline': ruler.padInline,

    '--lecteur-small-size': ruler.small[0],
    '--lecteur-small-line': ruler.small[1],
    '--lecteur-normal-size': ruler.normal[0],
    '--lecteur-normal-line': ruler.normal[1],
    '--lecteur-large-size': ruler.large[0],
    '--lecteur-large-line': ruler.large[1],
  };
}

/**
 * The one cast of the folder.
 *
 * React's `CSSProperties` declares the known properties and no index
 * signature, so a map of `--lecteur-*` keys - which the DOM accepts and which
 * is the entire mechanism of §9.4 here - has no type that satisfies it. The
 * cast is made once, in the module that builds the map, rather than at each of
 * the three places that hand one to an element.
 */
export function asStyle(variables: ReaderCssVariables): CSSProperties {
  return variables as unknown as CSSProperties;
}
